import { useState, useCallback, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  RefreshControl,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import {
  AgendaCalendar,
  EventCard,
  CreateEventModal,
} from "@/modules/agenda";
import { useAgendaEvents } from "@/modules/agenda/hooks/useAgendaEvents";
import { AgendaEvent } from "@/types";
import { isSameDay, format } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Agenda Screen
 * Main calendar view with event list
 */
export default function AgendaScreen() {
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { events, isLoading, error, refetch, fetchEventsForMonth } = useAgendaEvents();

  /**
   * Get events for selected date
   */
  const selectedDateEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = new Date(event.starts_at);
      return isSameDay(eventDate, selectedDate);
    });
  }, [events, selectedDate]);

  /**
   * Get upcoming events (next 7 days)
   */
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return events
      .filter((event) => {
        const eventDate = new Date(event.starts_at);
        return eventDate >= now && eventDate <= weekFromNow;
      })
      .slice(0, 5);
  }, [events]);

  /**
   * Handle date selection
   */
  const handleSelectDate = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  /**
   * Handle month change in calendar
   */
  const handleMonthChange = useCallback(
    (date: Date) => {
      fetchEventsForMonth(date);
    },
    [fetchEventsForMonth]
  );

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  /**
   * Handle event press
   */
  const handleEventPress = useCallback(
    (event: AgendaEvent) => {
      router.push(`/agenda/${event.id}`);
    },
    [router]
  );

  /**
   * Handle create event
   */
  const handleCreateEvent = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  /**
   * Handle event created
   */
  const handleEventCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View className="flex-row items-center justify-between px-4 py-3">
        <TouchableOpacity onPress={() => router.back()} accessibilityLabel="Terug">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text className="text-xl font-bold" style={{ color: colors.text }}>
          Agenda
        </Text>
        <TouchableOpacity
          onPress={handleCreateEvent}
          className="p-2 rounded-full"
          style={{ backgroundColor: colors.primary }}
          accessibilityLabel="Nieuw evenement"
          accessibilityRole="button"
        >
          <Ionicons name="add" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Calendar */}
        <View className="px-4 mt-2">
          <AgendaCalendar
            events={events}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            onMonthChange={handleMonthChange}
            isLoading={isLoading}
          />
        </View>

        {/* Selected Date Events */}
        <View className="px-4 mt-6">
          <View className="flex-row items-center justify-between mb-3">
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              {format(selectedDate, "EEEE d MMMM", { locale: nl })}
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {selectedDateEvents.length} evenement{selectedDateEvents.length !== 1 ? "en" : ""}
            </Text>
          </View>

          {selectedDateEvents.length === 0 ? (
            <View
              className="rounded-xl p-6 items-center"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="calendar-outline" size={40} color={colors.textTertiary} />
              <Text
                className="text-center mt-3 font-medium"
                style={{ color: colors.textSecondary }}
              >
                Geen evenementen op deze dag
              </Text>
              <TouchableOpacity
                onPress={handleCreateEvent}
                className="mt-4 px-4 py-2 rounded-full"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <Text className="font-medium" style={{ color: colors.primary }}>
                  + Voeg toe
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View>
              {selectedDateEvents.map((event) => (
                <View key={event.id} className="mb-3">
                  <EventCard event={event} onPress={() => handleEventPress(event)} />
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Upcoming Events Section */}
        {upcomingEvents.length > 0 && (
          <View className="px-4 mt-6">
            <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
              Binnenkort
            </Text>
            {upcomingEvents.map((event) => (
              <View key={event.id} className="mb-3">
                <EventCard event={event} onPress={() => handleEventPress(event)} compact />
              </View>
            ))}
          </View>
        )}

        {/* Error State */}
        {error && (
          <View
            className="mx-4 mt-4 p-4 rounded-xl"
            style={{ backgroundColor: colors.errorLight }}
          >
            <Text style={{ color: colors.error }}>{error}</Text>
            <TouchableOpacity onPress={refetch} className="mt-2">
              <Text className="font-medium" style={{ color: colors.error }}>
                Opnieuw proberen
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Bottom padding */}
        <View className="h-8" />
      </ScrollView>

      {/* Create Event Modal */}
      <CreateEventModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onEventCreated={handleEventCreated}
        initialDate={selectedDate}
      />
    </SafeAreaView>
  );
}
